import React, { useMemo } from "react";
import { Handbag, CheckCircle } from "lucide-react";
import type { ClientePedidoStatusRow } from "../utils/mapPainelParaColunasCliente";
import { formatTvHoraIso } from "../utils/formatTvHora";
import { useFilaTvAutoScroll } from "../hooks/useFilaTvAutoScroll";
import { CLASSE_LISTA_FILA_TV } from "../utils/filaTvListaScrollClasses";

interface FilaProntosRetiradaProps {
  itens: ClientePedidoStatusRow[];
}

export const FilaProntosRetirada: React.FC<FilaProntosRetiradaProps> = ({
  itens,
}) => {
  const listaFingerprint = useMemo(
    () => itens.map((r) => r.displayId).join(","),
    [itens]
  );
  const { listaRef, rolagemAutomatica } = useFilaTvAutoScroll(
    itens.length,
    listaFingerprint
  );

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border-2 border-white/25 bg-white/10 p-5 shadow-xl backdrop-blur-md sm:rounded-3xl sm:p-7 lg:border-[3px] lg:p-9">
      <div className="mb-5 shrink-0 space-y-3 sm:mb-7 sm:space-y-4">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <div className="rounded-full bg-primary-dark p-2.5 sm:p-4 lg:p-4">
            <Handbag className="h-7 w-7 text-white sm:h-10 sm:w-10 lg:h-11 lg:w-11" />
          </div>
          <h2 className="text-center text-2xl font-black leading-tight sm:text-4xl lg:text-5xl">
            Pronto para retirar ({itens.length})
          </h2>
        </div>
      </div>

      {itens.length === 0 ? (
        <div className="py-10 text-center sm:py-14">
          <div className="mb-6 flex justify-center opacity-45">
            <CheckCircle className="h-20 w-20 sm:h-28 sm:w-28 lg:h-36 lg:w-36" />
          </div>
          <p className="text-xl font-medium opacity-80 sm:text-2xl lg:text-3xl">
            Nenhum pedido pronto no balcão
          </p>
        </div>
      ) : (
        <div
          ref={listaRef}
          role="region"
          aria-label={
            rolagemAutomatica
              ? "Pedidos prontos para retirar, lista com mais de três itens, rolagem automática em ciclo"
              : "Pedidos prontos para retirar"
          }
          className={`${CLASSE_LISTA_FILA_TV} rounded-xl border-2 border-white/15 bg-transparent p-4 sm:rounded-2xl sm:p-6 lg:p-8`}
        >
          {itens.map((row) => (
            <div
              key={row.displayId}
              className="flex items-center gap-4 rounded-xl border-2 border-green-400/50 bg-green-500/35 px-5 py-4 shadow-md sm:gap-5 sm:rounded-2xl sm:px-7 sm:py-5 lg:px-8 lg:py-6"
            >
              <span
                className="shrink-0 text-3xl leading-none sm:text-4xl lg:text-5xl"
                aria-hidden
              >
                🍕
              </span>
              <div className="min-w-0">
                <span className="block break-words text-lg font-bold text-green-200 sm:text-2xl lg:text-3xl xl:text-4xl">
                  {row.customerName}
                </span>
                <span className="mt-1 block text-sm font-bold text-green-100 sm:text-lg lg:text-xl">
                  Pedido #{row.displayId} — retire no balcão
                </span>
                <span className="mt-1 block text-xs font-semibold tracking-wide text-green-50 sm:text-base lg:text-lg">
                  Liberado para retirada às{" "}
                  {formatTvHoraIso(row.prontoParaRetiradaEm)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
